import { record } from 'rrweb';
import type { ClintplayEvent } from './types';

type EventCallback = (event: ClintplayEvent) => boolean | void;

/**
 * Default interval for rrweb's periodic FullSnapshot ("checkout").
 *
 * Why this exists: a FullSnapshot (rrweb type 2) is the only thing the replay
 * viewer can seek to — IncrementalSnapshots (type 3) are mutations applied on
 * top of a snapshot. Without a periodic checkout, a long single-page session
 * has exactly ONE snapshot base (taken at startup), so seeking to a late point
 * forces the player to synchronously replay EVERY intervening mutation on the
 * main thread. On a heavy SPA (e.g. a CRM inbox re-rendering hundreds of nodes
 * per poll) that freezes the player on the initial frame — exactly the prod
 * incident this fixes (a 5.7h /chat session stuck on the atendimento screen).
 *
 * checkoutEveryNms only emits on the next event AFTER the interval elapses, so
 * an idle tab produces no extra snapshots — the storage cost is bounded to one
 * snapshot per `interval` of ACTIVE time. 2 min keeps any seek to ≤2 min of
 * replay while staying far below per-event-snapshot overhead. Override via
 * `rrwebConfig.checkoutEveryNms` (or `checkoutEveryNth`) per integration.
 */
export const DEFAULT_REPLAY_CHECKPOINT_MS = 120_000;
export const EMERGENCY_SNAPSHOT_COOLDOWN_MS = 5000;

export class Recorder {
  private stopFn: (() => void) | null = null;
  private isRecovering = false;
  private lastEmergencySnapshotTime = 0;

  constructor(
    private sessionId: string,
    private emit: EventCallback,
    private rrwebConfig?: Record<string, any>
  ) {}

  start(): void {
    try {
      this.stopFn = record({
        // Periodic checkpoint so long sessions stay seekable. Listed before
        // the spread so an integration's rrwebConfig can override (or disable
        // with `checkoutEveryNms: undefined`). See DEFAULT_REPLAY_CHECKPOINT_MS.
        checkoutEveryNms: DEFAULT_REPLAY_CHECKPOINT_MS,
        ...this.rrwebConfig,
        emit: (event: any) => {
          // rrweb does not wrap its `wrappedEmit` calls in try/catch around
          // the user-supplied callback. If we let an exception escape on the
          // FullSnapshot emit, rrweb skips initObservers() entirely — the
          // session ends up with only a Meta event and no replay. Swallow
          // here so observers always get attached.
          try {
            const ok = this.emit({
              type: 'rrweb',
              timestamp: event.timestamp,
              sessionId: this.sessionId,
              data: event,
            });
            if (ok === false && event.type !== 2) {
              this.triggerEmergencySnapshot();
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[Clintplay] rrweb emit failed', err);
            if (event.type !== 2) {
              this.triggerEmergencySnapshot();
            }
          }
        },
      }) ?? null;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[Clintplay] rrweb record() failed to start', err);
      this.stopFn = null;
    }
  }

  /**
   * Rate-limited snapshot triggered during error recovery when an incremental
   * mutation emit throws. Prevents snapshot storms if serialization repeatedly fails.
   */
  private triggerEmergencySnapshot(): void {
    const now = Date.now();
    const elapsed = now - this.lastEmergencySnapshotTime;
    if (elapsed >= 0 && elapsed < EMERGENCY_SNAPSHOT_COOLDOWN_MS) return;

    this.lastEmergencySnapshotTime = now;
    this.takeFullSnapshot();
  }

  /** Force a new FullSnapshot (useful for async content, font inlining, or SPA navigation). */
  takeFullSnapshot(): void {
    if (!this.stopFn || this.isRecovering) return;

    this.isRecovering = true;
    try {
      record.takeFullSnapshot();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[Clintplay] rrweb takeFullSnapshot failed', err);
    } finally {
      this.isRecovering = false;
    }
  }

  stop(): void {
    if (this.stopFn) {
      this.stopFn();
      this.stopFn = null;
    }
  }
}
